import { ButtonBase, Menu, MenuItem } from '@mui/material';
import { DataGridProps, GridRenderCellParams } from '@mui/x-data-grid';
import { JSX, useState } from 'react';
import { Option } from '../../types/option';
import { useQuery } from '@tanstack/react-query';

export interface DataGridEditOptionsCellProps extends GridRenderCellParams {
  /**
   * Persist changes via "processRowUpdate" callback
   */
  processRowUpdate: NonNullable<DataGridProps['processRowUpdate']>;
  /**
   * API endpoint to load the options from.
   */
  optionsEndpoint: string;

  /**
   * Exclude option IDs from the dropdown
   */
  excludeOptionIds?: number[];
}

/**
 * Component for rendering cell for editable value that is selected from options (loaded from API)
 */
const DataGridEditOptionsCell = ({
  value,
  field,
  row,
  processRowUpdate,
  excludeOptionIds = [],
  optionsEndpoint,
}: DataGridEditOptionsCellProps): JSX.Element => {
  const { data: options } = useQuery<Option[]>({
    queryKey: [optionsEndpoint],
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const selectedOption = options?.find((option) => option.id === value);
  const handleSelectOption = (id: number): void => {
    setAnchorEl(null);
    processRowUpdate({ ...row, [field]: id }, row, { rowId: row.id });
  };

  return (
    <>
      <ButtonBase
        className="w-full h-full"
        onClick={({ currentTarget }): void => setAnchorEl(currentTarget)}
      >
        {selectedOption?.note}
      </ButtonBase>
      {anchorEl && (
        <Menu anchorEl={anchorEl} open onClose={(): void => setAnchorEl(null)}>
          {options
            ?.filter(({ id }) => !excludeOptionIds.includes(id))
            .map((option) => (
              <MenuItem key={option.id} onClick={(): void => handleSelectOption(option.id)}>
                {option.note}
              </MenuItem>
            ))}
        </Menu>
      )}
    </>
  );
};

export default DataGridEditOptionsCell;
